import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { StatusFilter } from "@/components/StatusFilter";
import { usePersistedState } from "@/lib/usePersistedState";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash, UploadSimple, ShieldCheck } from "@phosphor-icons/react";
import ImportModal from "@/components/ImportModal";
import ClientCoiModal, { CoiPill, coiStatus } from "@/components/ClientCoiModal";
import { QuickView, QVRow, qvRowClick } from "@/components/QuickView";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import { SortableTh } from "@/components/SortableTh";

const empty = { name: "", initials: "", billing_email: "", contact_name: "", contact_email: "", contact_phone: "", address: "", default_payment_terms: "Net 30", auto_invoice: false, status: "active" };

const TERMS_OPTIONS = ["Net 15", "Net 30", "Net 45", "Net 60", "Due on Receipt"];

const IMPORT_FIELDS = [
  { key: "name", label: "Name", match: ["company", "client", "name"] },
  { key: "initials", label: "Initials", match: ["initials", "abbrev"] },
  { key: "billing_email", label: "Billing Email", match: ["billing", "email", "e-mail"] },
  { key: "contact_name", label: "Contact Name", match: ["contact name", "contact"] },
  { key: "contact_email", label: "Contact Email", match: ["contact email"] },
  { key: "contact_phone", label: "Contact Phone", match: ["contact phone", "phone", "cell", "mobile"] },
  { key: "address", label: "Address", match: ["address", "addr", "street", "city"] },
];

const LABELS = {
  name: "Name",
  initials: "Initials",
  billing_email: "Billing Email",
  contact_name: "Client Contact Name",
  contact_email: "Client Contact Email",
  contact_phone: "Client Contact Phone",
  address: "Address",
};

export default function Clients() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [viewC, setViewC] = useState(null);
  const [coiFor, setCoiFor] = useState(null);
  const [projFilter, setProjFilter] = usePersistedState("sos-filter-clients", "with_active");
  const readOnly = user.role !== "admin";
  const canEditCoi = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/clients").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);
  const filteredRows = useMemo(() => {
    if (projFilter === "with_active") return rows.filter((c) => (c.active_projects || 0) > 0);
    if (projFilter === "no_active") return rows.filter((c) => !(c.active_projects || 0));
    return rows;
  }, [rows, projFilter]);
  const filterCounts = useMemo(() => ({
    with_active: rows.filter((c) => (c.active_projects || 0) > 0).length,
    no_active: rows.filter((c) => !(c.active_projects || 0)).length,
    all: rows.length,
  }), [rows]);
  const { sorted, sortKey, sortDir, toggle } = useTableSort(filteredRows, {
    initialKey: "name", storageKey: "sos-sort-clients",
    getValue: (row, key) => (key === "coi" ? `${{ expired: 0, none: 1, current: 2 }[coiStatus(row.coi)]}-${row.coi?.expires_on || ""}` : row[key]),
  });
  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (editingId) await api.put(`/clients/${editingId}`, form);
      else await api.post("/clients", form);
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete client "${c.name}" permanently?\n\nThis cannot be undone. If they still have any projects, invoices, or timesheets, the delete will be refused — set them to Inactive instead.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success(`Deleted ${c.name}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div><div className="tinylabel">Master data</div><h1 className="text-3xl font-black">Clients</h1></div>
        {!readOnly && (
          <div className="flex gap-2">
            <button data-testid="import-clients-btn" onClick={() => setImportOpen(true)}
              className="h-11 px-4 border border-slate-300 hover:bg-slate-50 font-semibold rounded-md flex items-center gap-2">
              <UploadSimple size={18} weight="bold" /> Import
            </button>
            <button data-testid="new-client-btn" onClick={() => { setEditingId(null); setForm(empty); setOpen(true); }}
              className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} /> New client</button>
          </div>
        )}
      </div>

      <div className="mb-4">
        <StatusFilter
          value={projFilter}
          onChange={setProjFilter}
          counts={filterCounts}
          options={[
            { value: "with_active", label: "Active Projects" },
            { value: "no_active", label: "No Active Projects" },
            { value: "all", label: "All" },
          ]}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <SortableTh label="Name" colKey="name" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              {!isManager && <SortableTh label="Initials" colKey="initials" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              <SortableTh label="Phone" colKey="contact_phone" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              {!isManager && <SortableTh label="Billing" colKey="billing_email" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              <SortableTh label="Contact" colKey="contact_email" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              <SortableTh label="COI" colKey="coi" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td className="px-4 py-6 text-slate-400" colSpan={7}>No clients match this filter.</td></tr>}
            {sorted.map((c) => (
              <tr key={c.id} onClick={(e) => !readOnly && qvRowClick(e, () => setViewC(c))}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!readOnly ? "cursor-pointer" : ""}`} data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">
                  {c.name}
                  {c.auto_invoice && (
                    <span data-testid={`client-auto-invoice-badge-${c.id}`}
                          className="ml-2 inline-block text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200"
                          title="Approved timesheets for this client auto-generate a draft invoice">
                      Auto-invoice
                    </span>
                  )}
                </td>
                {!isManager && <td className="px-4 py-3 font-mono">{c.initials}</td>}
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.contact_phone || "—"}</td>
                {!isManager && <td className="px-4 py-3 text-slate-600">{c.billing_email || "—"}</td>}
                <td className="px-4 py-3 text-slate-600">{c.contact_email}</td>
                <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button type="button" data-testid={`client-coi-btn-${c.id}`} onClick={() => setCoiFor(c)}
                          title="Certificate of Insurance — view, send or upload"
                          className="inline-flex items-center gap-1.5 hover:opacity-80">
                    <CoiPill coi={c.coi} testId={`client-coi-pill-${c.id}`} />
                    <ShieldCheck size={16} weight="bold" className="text-slate-400" />
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!readOnly && <button data-testid={`edit-client-${c.id}`} onClick={() => { setEditingId(c.id); setForm({ ...empty, ...c }); setOpen(true); }} className="text-green-600 hover:text-green-800 font-semibold">Edit</button>}
                  {!readOnly && (
                    <button data-testid={`delete-client-${c.id}`} onClick={() => del(c)}
                            title="Delete client"
                            className="ml-3 text-slate-400 hover:text-rose-600 align-middle">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={save} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-bold">{editingId ? "Edit client" : "New client"}</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button></div>
            {[["name", true], ["initials", true], ["billing_email"], ["contact_name"], ["contact_email"], ["contact_phone"], ["address"]].map(([k, req]) => (
              <div className="mb-3" key={k}>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{LABELS[k] || k.replace(/_/g, " ")}</label>
                <input required={req} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={k === "billing_email" ? "one or more emails, comma-separated" : undefined}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" data-testid={`client-input-${k}`} />
              </div>
            ))}
            <div className="mb-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Default Payment Terms</label>
              <select value={form.default_payment_terms || "Net 30"}
                onChange={(e) => setForm({ ...form, default_payment_terms: e.target.value })}
                data-testid="client-input-default_payment_terms"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                {TERMS_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="mt-1 text-xs text-slate-500">Applied automatically when weekly invoices are generated for this client.</div>
            </div>
            <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="client-input-auto_invoice"
                  checked={!!form.auto_invoice}
                  onChange={(e) => setForm({ ...form, auto_invoice: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                />
                <div>
                  <div className="text-sm font-bold text-emerald-900">Auto-invoice on approval</div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    When a supervisor approves a timesheet for this client, immediately create a <b>draft invoice</b> for it. The invoice still requires your review + send from the Invoices tab.
                  </div>
                </div>
              </label>
            </div>
            {err && <div className="text-red-600 text-sm">{err}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="save-client-btn" className="h-11 px-4 bg-green-600 text-white font-semibold rounded-md">Save</button>
            </div>
          </form>
        </div>
      )}
      {importOpen && (
        <ImportModal title="Import clients" endpoint="/clients/import" fields={IMPORT_FIELDS} noun="client"
                     onClose={() => setImportOpen(false)} onImported={load} />
      )}
      {viewC && (
        <QuickView title={viewC.name} subtitle={viewC.initials ? `Initials: ${viewC.initials}` : null} status={viewC.status}
                   onClose={() => setViewC(null)} testId="client-quick-view">
          <QVRow label="Billing email" value={viewC.billing_email} />
          <QVRow label="Contact name" value={viewC.contact_name} />
          <QVRow label="Contact email" value={viewC.contact_email} />
          <QVRow label="Contact phone" value={viewC.contact_phone} mono />
          <QVRow label="Address" value={viewC.address} />
          <QVRow label="Payment terms" value={viewC.default_payment_terms} />
          <QVRow label="Auto-invoice" value={viewC.auto_invoice ? "On" : "Off"} />
          <QVRow label="Active projects" value={viewC.active_projects} mono />
          <QVRow label="Certificate of Insurance" value={<CoiPill coi={viewC.coi} testId="client-qv-coi-pill" />} />
        </QuickView>
      )}
      {coiFor && (
        <ClientCoiModal client={coiFor} canEdit={canEditCoi} canDelete={user.role === "admin"}
                        onClose={() => setCoiFor(null)} onChanged={load} />
      )}
    </div>
  );
}
